import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../ui/Card';
import { Button } from '../ui/Button';
import { AccountFormField } from '../ui/AccountFormField';
import { FormErrorMessage } from '../ui/FormErrorMessage';
import { StarRating } from '../ui/StarRating';
import { Bug, Lightbulb, AlertTriangle, MessageCircle } from 'lucide-react';
import { useFeedbackSubmit } from '../../hooks/useFeedbackSubmit';

interface FeedbackFormProps {
  userEmail: string;
}

type FeedbackType = 'bug' | 'feature' | 'wrong_info' | 'other';

interface FeedbackTypeOption {
  value: FeedbackType;
  label: string;
  description: string;
  icon: React.ReactNode;
}

const FEEDBACK_TYPES: FeedbackTypeOption[] = [
  {
    value: 'bug',
    label: 'Bug Report',
    description: 'Something isn\'t working correctly',
    icon: <Bug className="w-4 h-4" />,
  },
  {
    value: 'feature',
    label: 'Feature Request',
    description: 'Suggest a new feature or improvement',
    icon: <Lightbulb className="w-4 h-4" />,
  },
  {
    value: 'wrong_info',
    label: 'Wrong Information',
    description: 'Incorrect stock data or hints',
    icon: <AlertTriangle className="w-4 h-4" />,
  },
  {
    value: 'other',
    label: 'Other',
    description: 'General feedback or questions',
    icon: <MessageCircle className="w-4 h-4" />,
  },
];

export const FeedbackForm: React.FC<FeedbackFormProps> = ({ userEmail }) => {
  const [feedbackType, setFeedbackType] = useState<FeedbackType>('bug');
  const [feedbackText, setFeedbackText] = useState('');
  const [contactEmail, setContactEmail] = useState(userEmail);
  const [rating, setRating] = useState<number | null>(null);

  const { submitFeedback, isSubmitting, error, success, clearError, clearSuccess } = useFeedbackSubmit();

  // Clear form inputs immediately on success
  useEffect(() => {
    if (success) {
      setFeedbackText('');
      setFeedbackType('bug');
      setContactEmail(userEmail);
      setRating(null);
    }
  }, [success, userEmail]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isFormValid()) {
      return;
    }

    await submitFeedback({
      feedbackType,
      feedbackText: feedbackText.trim(),
      contactEmail: contactEmail.trim(),
      rating: rating ?? undefined,
    });
  };

  const isFormValid = () => {
    const emailTrimmed = contactEmail.trim();
    const emailValid = emailTrimmed.length === 0 || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrimmed);
    return (
      feedbackText.trim().length >= 10 &&
      feedbackText.trim().length <= 5000 &&
      emailValid &&
      !isSubmitting
    );
  };

  return (
    <form onSubmit={handleSubmit}>
      <Card>
        <CardHeader className="pb-3 sm:pb-4">
          <CardTitle className="text-lg sm:text-xl">Feedback Details</CardTitle>
          <CardDescription className="text-sm">
            Tell us what's on your mind
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4 sm:space-y-6">
          {/* Error Message */}
          {error && (
            <FormErrorMessage
              error={error}
              onDismiss={clearError}
              variant="banner"
            />
          )}

          {/* Success Message */}
          {success && (
            <div className="text-center py-8 space-y-3">
              <p className="text-lg sm:text-xl font-semibold text-green-600 dark:text-green-400">
                Thank you for submitting feedback!
              </p>
              <p className="text-sm text-muted-foreground">
                Your feedback helps us improve Wallstreetle.
              </p>
              <Button
                type="button"
                variant="outline"
                onClick={clearSuccess}
                className="mt-4"
              >
                Submit More Feedback
              </Button>
            </div>
          )}

          {/* Feedback Type Selection */}
          {!success && (<>
          <div className="space-y-2">
            <label className="block text-sm sm:text-base font-medium text-foreground">
              What type of feedback is this?
              <span className="text-red-500 ml-1">*</span>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
              {FEEDBACK_TYPES.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => setFeedbackType(type.value)}
                  disabled={isSubmitting}
                  className={`
                    flex items-start gap-3 p-3 sm:p-4 border-2 rounded-lg text-left transition-all
                    mobile-touch-target
                    ${
                      feedbackType === type.value
                        ? 'border-primary bg-primary/5 dark:bg-primary/10'
                        : 'border-muted hover:border-primary/50 bg-background'
                    }
                    ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                  `}
                >
                  <div className="flex-shrink-0 mt-0.5">{type.icon}</div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm sm:text-base text-foreground">
                      {type.label}
                    </p>
                    <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                      {type.description}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Overall Experience Rating (Optional) */}
          <div className="space-y-2">
            <label className="block text-sm sm:text-base font-medium text-foreground">
              How would you rate your overall experience?
              <span className="text-sm text-muted-foreground ml-2">(Optional)</span>
            </label>
            <StarRating
              rating={rating}
              onChange={setRating}
              size="md"
              showLabel={true}
              allowClear={true}
              disabled={isSubmitting}
            />
          </div>

          {/* Feedback Text */}
          <div className="space-y-2">
            <label
              htmlFor="feedback-text"
              className="block text-sm sm:text-base font-medium text-foreground"
            >
              Your Feedback
              <span className="text-red-500 ml-1">*</span>
            </label>
            <textarea
              id="feedback-text"
              value={feedbackText}
              onChange={(e) => {
                setFeedbackText(e.target.value);
                if (error) clearError();
              }}
              placeholder="Please provide as much detail as possible..."
              rows={6}
              maxLength={5000}
              required
              disabled={isSubmitting}
              className="
                w-full px-3 py-2 sm:py-3 border rounded-md shadow-sm
                text-base sm:text-sm
                mobile-touch-target
                focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary
                disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed
                bg-background text-foreground placeholder:text-muted-foreground
                border-gray-200 dark:border-gray-800
                resize-y
              "
            />
            <p className="text-xs sm:text-sm text-muted-foreground">
              {feedbackText.length} / 5000 characters
              {feedbackText.length < 10 && feedbackText.length > 0 && (
                <span className="text-yellow-600 dark:text-yellow-400 ml-2">
                  (minimum 10 characters)
                </span>
              )}
            </p>
          </div>

          {/* Contact Email */}
          <AccountFormField
            id="contact-email"
            label={<>Contact Email <span className="text-sm text-muted-foreground">(Optional)</span></>}
            type="email"
            value={contactEmail}
            onChange={(value) => {
              setContactEmail(value);
              if (error) clearError();
            }}
            placeholder="your-email@example.com"
            disabled={isSubmitting}
            helperText="Optional — we'll use this to follow up on your feedback if needed"
          />
          </>
          )}
        </CardContent>

        {!success && (
        <CardFooter className="pt-4 sm:pt-6">
          <Button
            type="submit"
            disabled={!isFormValid()}
            className="w-full mobile-touch-target"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
          </Button>
        </CardFooter>
        )}
      </Card>
    </form>
  );
};

export default FeedbackForm;
